// 'fooMilliseconds' -> 'fooSeconds' (fallback: append 'Seconds').
function deriveSecondsKey(msKey) {
    return (/Milliseconds$/).test(msKey)
        ? msKey.replace(/Milliseconds$/, 'Seconds')
        : `${msKey}Seconds`;
}
/**
 * Builds the field pair for an interval an operator sets in seconds but the
 * device config stores in milliseconds.
 *
 * The milliseconds field is hidden and is the saved source of truth; the seconds
 * field is display-only (persistent: false, stripped on save by
 * DynamicFormsService.stripNonPersistentFields). Both use a plain calculateValue
 * -- NOT allowCalculateOverride, which Form.io skips on the initial submission
 * load -- and the ms field is returned FIRST so Form.io recalculates it before
 * the seconds field, which preserves an operator's edit instead of reverting it.
 *
 * @param {SecondsFieldConfig} config the milliseconds key, label and bounds.
 * @returns {ExtendedComponentSchema[]} [millisecondsField, secondsField] to spread into a form.
 */
export function secondsFieldStoredAsMilliseconds(config) {
    const { msKey, label, secondsKey = deriveSecondsKey(msKey), description, defaultSeconds = 60, min = 1, max, required = true, } = config;
    const millisecondsField = {
        key: msKey,
        type: 'hidden',
        calculateServer: false,
        // typeof 'number' rather than truthiness so a valid 0 converts (0 -> 0) while
        // an empty field keeps the existing ms value (needed on the initial load,
        // before the seconds field has been seeded).
        calculateValue: `value = typeof data.${secondsKey} === 'number' ? data.${secondsKey} * 1000 : value;`,
    };
    const validate = { required, min };
    if (typeof max === 'number') {
        validate.max = max;
    }
    const secondsField = {
        label,
        key: secondsKey,
        type: 'number',
        persistent: false,
        calculateServer: false,
        // typeof 'number' so a stored 0 ms shows 0 (not the default); an unset value
        // falls back to the default seconds. No rounding, so a non-1000ms-aligned
        // value (e.g. 1500ms -> 1.5s) round-trips instead of being silently changed.
        calculateValue: `value = typeof data.${msKey} === 'number' ? data.${msKey} / 1000 : ${defaultSeconds};`,
        validate,
    };
    if (description) {
        secondsField.description = description;
    }
    return [millisecondsField, secondsField];
}
